import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem,
    Typography, Box, FormControlLabel, Switch, InputAdornment, IconButton,
    CircularProgress, Alert
} from '@mui/material';
import { useNotify } from 'react-admin';
import axios from 'axios';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import config from '../../config/config.json';
import ColorLensIcon from '@mui/icons-material/ColorLens';

const host = config.production.host;
const getJwt = () => localStorage.getItem("feathers-jwt");

export const UsersGroupSettingsDialog = ({ open, onClose, settings, onSave }) => {
    const notify = useNotify();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [leagues, setLeagues] = useState([]);
    const [teams, setTeams] = useState([]);

    // Form state
    const [formData, setFormData] = useState({});

    // Initialize form data when settings change
    useEffect(() => {
        if (settings) {
            let primary = settings.primaryColor || '#000000';
            let secondary = settings.secondaryColor || '#000000';
            let accent = settings.accentColor || '#000000';

            // Try to parse colorPalette if available
            if (settings.colorPalette) {
                try {
                    const palette = typeof settings.colorPalette === 'string'
                        ? JSON.parse(settings.colorPalette)
                        : settings.colorPalette;

                    if (palette.primary) primary = palette.primary;
                    if (palette.secondary) secondary = palette.secondary;
                    if (palette.accent) accent = palette.accent;
                } catch (e) {
                    console.error("Error parsing colorPalette:", e);
                }
            }

            setFormData({
                ...settings,
                selectedLegaId: settings.selectedLegaId || '',
                selectedTeamId: settings.selectedTeamId || '',
                primaryColor: primary,
                secondaryColor: secondary,
                accentColor: accent
            });
        }
    }, [settings]);

    // Load leagues and teams
    useEffect(() => {
        if (open) {
            loadLeaguesAndTeams();
        }
    }, [open]);

    const loadLeaguesAndTeams = async () => {
        try {
            setLoading(true);
            const jwt = getJwt();
            const [leaguesRes, teamsRes] = await Promise.all([
                axios.get(`${host}/tbl-lega?$limit=50&$sort[nome]=1`, { headers: { 'Authorization': `Bearer ${jwt}` } }),
                axios.get(`${host}/tbl-team?$limit=100&$sort[nome]=1`, { headers: { 'Authorization': `Bearer ${jwt}` } })
            ]);
            setLeagues(leaguesRes.data.data || []);
            setTeams(teamsRes.data.data || []);
        } catch (error) {
            console.error("Error loading metadata", error);
            notify("Errore nel caricamento dei dati di supporto", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const jwt = getJwt();

            // Find names for selected IDs
            const selectedLega = leagues.find(l => l.id === formData.selectedLegaId);
            const selectedTeam = teams.find(t => t.id === formData.selectedTeamId);

            // Construct colorPalette JSON
            const colorPaletteObj = {
                primary: formData.primaryColor,
                secondary: formData.secondaryColor,
                accent: formData.accentColor
            };

            const dataToUpdate = {
                ...formData,
                selectedLegaName: selectedLega?.nome || null,
                selectedTeamName: selectedTeam?.nome || null,
                colorPalette: JSON.stringify(colorPaletteObj)
            };

            // Remove internal or read-only fields if necessary
            delete dataToUpdate.id;
            delete dataToUpdate.createdAt;
            delete dataToUpdate.updatedAt;

            await axios.patch(`${host}/users-settings/${settings.id}`, dataToUpdate, {
                headers: { 'Authorization': `Bearer ${jwt}` }
            });

            notify("Impostazioni aggiornate con successo", { type: 'success' });
            if (onSave) onSave();
            onClose();

        } catch (error) {
            console.error("Error saving settings:", error);
            notify("Errore durante il salvataggio", { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (!settings) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Modifica Impostazioni Gruppo
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {/* Profilo & Contatti */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                                PROFILO & CONTATTI
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Nome Società / Visualizzato"
                                        value={formData.nomeSocieta || ''}
                                        onChange={(e) => handleChange('nomeSocieta', e.target.value)}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Tipo Account"
                                        value={formData.accountType || 'user'}
                                        size="small"
                                        disabled
                                        helperText="Derivato automaticamente dal gruppo"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Email Pubblica"
                                        value={formData.email || ''}
                                        onChange={(e) => handleChange('email', e.target.value)}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Telefono"
                                        value={formData.telefono || ''}
                                        onChange={(e) => handleChange('telefono', e.target.value)}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Indirizzo"
                                        value={formData.indirizzo || ''}
                                        onChange={(e) => handleChange('indirizzo', e.target.value)}
                                        size="small"
                                        sx={{ mb: 2 }}
                                    />
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="Città"
                                                value={formData.citta || ''}
                                                onChange={(e) => handleChange('citta', e.target.value)}
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="Provincia"
                                                value={formData.provincia || ''}
                                                onChange={(e) => handleChange('provincia', e.target.value)}
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="CAP"
                                                value={formData.cap || ''}
                                                onChange={(e) => handleChange('cap', e.target.value)}
                                                size="small"
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Preferenze Sportive */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                                PREFERENZE SPORTIVE & COLORI
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Lega</InputLabel>
                                        <Select
                                            value={formData.selectedLegaId || ''}
                                            label="Lega"
                                            onChange={(e) => {
                                                handleChange('selectedLegaId', e.target.value);
                                                handleChange('selectedTeamId', ''); // Reset team
                                            }}
                                        >
                                            <MenuItem value=""><em>Nessuna</em></MenuItem>
                                            {leagues.map(l => (
                                                <MenuItem key={l.id} value={l.id}>{l.nome}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth size="small" disabled={!formData.selectedLegaId}>
                                        <InputLabel>Squadra</InputLabel>
                                        <Select
                                            value={formData.selectedTeamId || ''}
                                            label="Squadra"
                                            onChange={(e) => handleChange('selectedTeamId', e.target.value)}
                                        >
                                            <MenuItem value=""><em>Nessuna</em></MenuItem>
                                            {teams
                                                .filter(t => !formData.selectedLegaId || t.legaId == formData.selectedLegaId)
                                                .map(t => (
                                                    <MenuItem key={t.id} value={t.id}>{t.nome}</MenuItem>
                                                ))
                                            }
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
                                        <ColorLensIcon color="action" />
                                        <input
                                            type="color"
                                            value={formData.primaryColor || '#000000'}
                                            onChange={e => handleChange('primaryColor', e.target.value)}
                                            style={{ cursor: 'pointer', width: 40, height: 30, padding: 0, border: 'none' }}
                                            title="Colore Primario"
                                        />
                                        <input
                                            type="color"
                                            value={formData.secondaryColor || '#000000'}
                                            onChange={e => handleChange('secondaryColor', e.target.value)}
                                            style={{ cursor: 'pointer', width: 40, height: 30, padding: 0, border: 'none' }}
                                            title="Colore Secondario"
                                        />
                                        <input
                                            type="color"
                                            value={formData.accentColor || '#000000'}
                                            onChange={e => handleChange('accentColor', e.target.value)}
                                            style={{ cursor: 'pointer', width: 40, height: 30, padding: 0, border: 'none' }}
                                            title="Colore Accento"
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Social Links */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                                SOCIAL LINKS
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Sito Web"
                                        value={formData.sitoWeb || ''}
                                        onChange={(e) => handleChange('sitoWeb', e.target.value)}
                                        size="small"
                                        placeholder="https://..."
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Facebook URL"
                                        value={formData.facebookUrl || ''}
                                        onChange={(e) => handleChange('facebookUrl', e.target.value)}
                                        size="small"
                                        placeholder="https://facebook.com/..."
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        label="Instagram URL"
                                        value={formData.instagramUrl || ''}
                                        onChange={(e) => handleChange('instagramUrl', e.target.value)}
                                        size="small"
                                        placeholder="https://instagram.com/..."
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Notifiche & Opzioni */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="primary" sx={{ mb: 2, fontWeight: 600 }}>
                                NOTIFICHE E OPZIONI
                            </Typography>
                            <Grid container spacing={1}>
                                <Grid item xs={12} sm={4}>
                                    <FormControlLabel
                                        control={<Switch checked={!!formData.notifiche_risultati} onChange={(e) => handleChange('notifiche_risultati', e.target.checked)} />}
                                        label="Notifiche Risultati"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControlLabel
                                        control={<Switch checked={!!formData.notifiche_news} onChange={(e) => handleChange('notifiche_news', e.target.checked)} />}
                                        label="Notifiche News"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControlLabel
                                        control={<Switch checked={!!formData.notifiche_live} onChange={(e) => handleChange('notifiche_live', e.target.checked)} />}
                                        label="Notifiche Live"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={<Switch checked={!!formData.profilo_pubblico} onChange={(e) => handleChange('profilo_pubblico', e.target.checked)} />}
                                        label="Profilo Pubblico"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={<Switch checked={!!formData.condiviso_statistiche} onChange={(e) => handleChange('condiviso_statistiche', e.target.checked)} />}
                                        label="Statistiche Condivise"
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">Annulla</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    disabled={saving}
                >
                    Salva Modifiche
                </Button>
            </DialogActions>
        </Dialog>
    );
};
